import { type IUnit } from '../types'
import { useAppSelector } from './redux'
import { type RootState } from '../store/store'
import { useMemo } from 'react'
import _ from 'lodash'
import { type User } from '../store/slices/userSlice'
import dayjs from 'dayjs'
import { getUnitSourceCategory } from '../functions/shared/units/getUnitSourceCategory'
import { isBsoRentalOrSaleUnit, isExternalRequestedUnit, isExternalUnit, isOperationallyActiveExternalUnit, isVisibleInAgentCatalog } from '../functions/shared/units/unitVisibility'

const getStrapiVal = (v: any) => v?.data?.attributes ?? v?.attributes ?? v

const useUnitsNew = (): IUnit[] => {
  const units = useAppSelector((s: RootState) => s.units.units)
  const searchString = useAppSelector((s: RootState) => s.units.searchString)
  const filters = useAppSelector((s: RootState) => s.units.filters)

  const myArchivedUnitsOpen = useAppSelector((s: RootState) => s.units.myArchivedUnitsOpen)
  const myRequestedUnitsOpen = useAppSelector((s: RootState) => s.units.myRequestedUnitsOpen)
  const myPublishedUnitsOpen = useAppSelector((s: RootState) => s.units.myPublishedUnitsOpen)
  const publishedVisibilityFilter = useAppSelector((s: RootState) => s.units.publishedVisibilityFilter)
  const currentUser = useAppSelector((s: RootState) => s.user.currentUser) as User
  const unitsCategory = useAppSelector((state: RootState) => state.units.unitsCategory)
  const unitsSourceFilter = useAppSelector((state: RootState) => state.units.unitsSourceFilter)
  const supportUserId = Number(process.env.REACT_APP_BSO_SUPPORT_USER_ID)

  const normalizedSearch = (searchString ?? '').trim().toLowerCase()

  const catalog = useMemo(() => {
    const meId = currentUser?.id
    if (!Array.isArray(units)) return []

    let filtered = units.filter((u) => {
      const a = u?.attributes
      if (!a) return false

      const statuses: string[] = Array.isArray(a?.Statuses) ? (a.Statuses as string[]) : []
      const sourceCategory = getUnitSourceCategory(u)

      if (myRequestedUnitsOpen) {
        if (!isExternalRequestedUnit(u)) return false
      } else if (myPublishedUnitsOpen) {
        const managedBy = String(a?.ManagedBy ?? '').toLowerCase()

        if (statuses.includes('Archived') || statuses.includes('Registered')) return false
        if (!isExternalUnit(u)) return false
        if (managedBy === 'bso') return false
        if (publishedVisibilityFilter === 'catalog') {
          if (!isVisibleInAgentCatalog(u)) return false
        } else if (!isOperationallyActiveExternalUnit(u) || isVisibleInAgentCatalog(u)) return false
      } else {
        if (!isBsoRentalOrSaleUnit(u)) return false

        const isArchived = statuses.includes('Archived')
        if (myArchivedUnitsOpen ? !isArchived : isArchived) return false
      }

      if ((myRequestedUnitsOpen || myPublishedUnitsOpen) && unitsSourceFilter !== 'all' && sourceCategory !== unitsSourceFilter) {
        return false
      }

      const isSupportUser = Number(meId) === supportUserId
      const shouldApplyUseCategoryFilter = !(myRequestedUnitsOpen && !isSupportUser)
      if (shouldApplyUseCategoryFilter) {
        if (unitsCategory === 'rent') {
          const useVal = a?.Marketing?.Use
          if (!(useVal === 'For rent' || useVal == null)) return false
        } else if (unitsCategory === 'sell') {
          const useVal = a?.Marketing?.Use
          if (!(useVal === 'For sale' || useVal == null)) return false
        }
      }

      if (normalizedSearch.length > 0) {
        const headline = a?.Marketing?.Headline?.toLowerCase?.() ?? ''
        const area = a?.Property?.data?.attributes?.Area?.toLowerCase?.() ?? ''
        const propName = a?.Property?.data?.attributes?.Name?.toLowerCase?.() ?? ''
        const s = normalizedSearch

        const hit =
          headline.includes(s) ||
          area.includes(s) ||
          propName.includes(s) ||
          s.includes(area) ||
          s.includes(propName)

        if (!hit) return false
      }

      return true
    })

    if (filters?.Bedrooms != null && filters.Bedrooms !== '') {
      const v = filters.Bedrooms
      filtered = filtered.filter((u) => {
        const a = u?.attributes
        const bedrooms = Number(a?.Bedrooms ?? 0)
        if (v === 'MORE-3') return bedrooms > 3
        if (Number.isFinite(Number(v))) return bedrooms === Number(v)
        return true
      })
    }

    if (filters?.Bathrooms != null && filters.Bathrooms !== '') {
      const v = filters.Bathrooms
      filtered = filtered.filter((u) => {
        const a = u?.attributes
        const bathrooms = Number(a?.Bathrooms ?? 0)
        if (v === 'MORE-3') return bathrooms > 3
        if (Number.isFinite(Number(v))) return bathrooms === Number(v)
        return true
      })
    }

    if (filters?.Type != null && filters.Type !== '') {
      filtered = filtered.filter((u) => {
        const a = u?.attributes
        return String(a?.Type ?? '').toLowerCase() === String(filters.Type).toLowerCase()
      })
    }

    if (filters?.['>Property.Area'] != null && Array.isArray(filters['>Property.Area']) && filters['>Property.Area'].length > 0) {
      const areas = new Set(filters['>Property.Area'].map((x: any) => String(x).toLowerCase()))
      filtered = filtered.filter((u) => {
        const a = u?.attributes
        const area = a?.Property?.data?.attributes?.Area
        if (!area) return false
        return areas.has(String(area).toLowerCase())
      })
    }

    if (filters?.['>Offers[0]_Statuses[0]'] != null && Array.isArray(filters['>Offers[0]_Statuses[0]']) && filters['>Offers[0]_Statuses[0]'].length > 0) {
      const wanted = new Set(filters['>Offers[0]_Statuses[0]'].map((x: any) => String(x)))
      filtered = filtered.filter((u) => {
        const a = u?.attributes
        const offersRaw = a?.Offers?.data ?? a?.Offers ?? []
        const offers = Array.isArray(offersRaw) ? offersRaw : []

        return offers.some((o: any) => {
          const offer = getStrapiVal(o)
          const stArr: any[] = offer?.Statuses ?? offer?.Status ?? []
          const list = Array.isArray(stArr) ? stArr : [stArr]
          return list.some((s) => wanted.has(String(s)))
        })
      })
    }

    const sortChoice = filters?.['SORT-by']
    const sortDir =
      sortChoice === 'Creation date ascending' ? 'asc'
      : sortChoice === 'Creation date descending' ? 'desc'
      : null

    const iteratees: any[] = [
      (u: any) => ((u?.attributes?.Statuses ?? []).includes('Sold') ? 1 : 0),
    ]
    const orders: any[] = ['asc']

    if (sortDir) {
      iteratees.push((u: any) => dayjs(u?.attributes?.CreateDate ?? u?.attributes?.createdAt).valueOf())
      orders.push(sortDir)
    } else {
      iteratees.push((u: any) => dayjs(u?.attributes?.updatedAt ?? u?.attributes?.CreateDate).valueOf())
      orders.push('desc')
    }

    return _.orderBy(filtered, iteratees, orders)
  }, [units, currentUser?.id, myRequestedUnitsOpen, myPublishedUnitsOpen, myArchivedUnitsOpen, publishedVisibilityFilter, unitsCategory, unitsSourceFilter, normalizedSearch, filters, supportUserId])

  return catalog
}

export default useUnitsNew
